import { Component, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { IConfiguracionLLaves, ICrudTablaDinamicaConfig, ITipoTramite } from '../../../../shared/interfaces/database/configuracion';
import { ControlDTO, IFormularioConControles } from 'lib-base';
import { TablasDinamicasConfigService } from '../../../../services/database/tablas-dinamicas-config.service';
import { FormularioService } from '../../../../services/database/formulario.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { ConfiguracionLlavesComponent, LlavesOptions } from '../../../../components/custom-work/other/configuracion-llaves/configuracion-llaves.component';
import { IColumnDinamico, TablasDinamicasConfig } from '../../../../shared/interfaces/database/tramites_automaticos';
import { ColumnType } from '../../datasets-dinamicos/dataset-dinamico-columnas';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-config-actualizacion-tabla-dinamica',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, PanelModule,
    DropdownModule, ConfiguracionLlavesComponent],
  template: `
    @for (item of formularioControles; track item.id; let idx = $index) {
      <div class="w-full mb-1">
        <p-panel [header]="item.nombre" 
            [toggleable]="true" [collapsed]="true"
            class="w-full mb-3">
          <div class="grid formgrid p-fluid">
            <div class="field col-12 md:col-12">          
              <label for="filtros" class="ml-3">{{'common.tablaDinamica' | translate}}</label>
              <div class="flex gap-1 justify-content-start">
                <p-dropdown [options]="tablasDinamicasConfig" appendTo="body"
                  [ngModel]="getTablaId(item.id)"
                  (ngModelChange)="setTablaId(item.id, $event)"
                  optionLabel="datasetNombre" optionValue="id" [placeholder]="'common.seleccionar'| translate"
                  autoWidth="false" class="flex-grow-1" [filter]="true" [showClear]="true"
                  (onChange)="onTablaParaActualizacionChange($event, idx)"
                  (onClear)="onTablaParaActualizacionClear(idx)">
                </p-dropdown> 
              </div>
            </div>
            <div class="field col-12 md:col-12">
              <app-configuracion-llaves #TablaFiltrosLlaves 
                [showDelete]="true" [showAdd]="true"
                [headerTitle]="'msg.mapeoDeFiltrosActualizacion' | translate">
              </app-configuracion-llaves>
            </div>
            <div class="field col-12 md:col-12">
              <app-configuracion-llaves #TablaParaActualizacionIdLlaves 
                [showDelete]="true" [showAdd]="true"
                [headerTitle]="'msg.mapeoDeCamposActualizacion' | translate">
              </app-configuracion-llaves>
            </div>
          </div>
        </p-panel>
      </div>
    }
  `
})
export class ConfigActualizacionTablaDinamicaComponent implements OnInit {
  @Input() tipoTramite!: ITipoTramite;

  formularioControles!: IFormularioConControles[];
  formularioControlesReady: boolean = false;
  tablasDinamicasConfig!: TablasDinamicasConfig[];
  tablasDinamicasConfigReady: boolean = false;
  toLoad: ICrudTablaDinamicaConfig[] | null = null;

  variablesSeguimiento: ControlDTO[] = [];

  tablaIdMap: Map<number, number> = new Map();
  @ViewChildren('TablaFiltrosLlaves', { read: ConfiguracionLlavesComponent }) tablaFiltrosLlaves!: QueryList<ConfiguracionLlavesComponent>;
  @ViewChildren('TablaParaActualizacionIdLlaves', { read: ConfiguracionLlavesComponent }) tablaParaActualizacionIdLlaves!: QueryList<ConfiguracionLlavesComponent>;

  trans: any = {};

  constructor(
    private _tdcs: TablasDinamicasConfigService,
    private _fs: FormularioService,
    private readonly _t: TranslateService
  ) { }

  ngOnInit(): void {
    this.translate();
    this.getTablasDinamicas();
    this.getForm();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      columnasTablaDinamica: this._t.get('common.columnasTablaDinamica'),
      controlesFormulario: this._t.get('common.controlesFormulario')
    }));
  }

  //#region dropdown

  getTablaId(formId: number): number | null {
    return this.tablaIdMap.get(formId) || null;
  }

  setTablaId(formId: number, tablaId: number | null): void {
    if (tablaId === null) {
      this.tablaIdMap.delete(formId);
    } else {
      this.tablaIdMap.set(formId, tablaId);
    }
  }

  onTablaParaActualizacionChange(event: any, index: number) {
    const value = event.value;
    if (!value) return;

    const component = this.tablaParaActualizacionIdLlaves.toArray()[index];
    if (component) {
      this.initializeTablaParaActualizacionLlaves(component, value, index, true);
    }

    const filtrosComponent = this.tablaFiltrosLlaves.toArray()[index];
    if (filtrosComponent) {
      this.initializeTablaParaActualizacionLlaves(filtrosComponent, value, index, false);
    }
  }

  onTablaParaActualizacionClear(index: number) {
    const component = this.tablaParaActualizacionIdLlaves.toArray()[index];
    if (component) {
      component.reset();
    }
    const componentFiltros = this.tablaFiltrosLlaves.toArray()[index];
    if (componentFiltros) {
      componentFiltros.reset();
    }

    const form = this.formularioControles[index];
    this.setTablaId(form.id, null);
  }

  //#endregion dropdown

  //#region get value
  getComponentValue(): ICrudTablaDinamicaConfig[] | null {
    const result: ICrudTablaDinamicaConfig[] = [];

    this.tablaFiltrosLlaves?.toArray().forEach((filtroComponent, index) => {
      const actualizacionComponent = this.tablaParaActualizacionIdLlaves.toArray()[index];
      const form = this.formularioControles[index];
      const filtroLLaves = (filtroComponent.getValues() || []).filter((item: any) => item.value !== '');
      const actualizacionLLaves = (actualizacionComponent.getValues() || []).filter((item: any) => item.value !== '');
      const tablaId = this.getTablaId(form.id);
      if (filtroLLaves.length > 0 && actualizacionLLaves.length > 0 && tablaId) {
        result.push({
          formularioId: form.id,
          formularioVersion: form.version,
          tablaDinamicaConfigId: tablaId,
          subformulario: form.subformulario,
          llaves: actualizacionLLaves,
          tipo: 'update',
          filtros: filtroLLaves
        });
      }
    });

    return result.length > 0 ? result : null;
  }

  //#endregion get value

  //#region load
  private getTablasDinamicas() {
    this._tdcs.getTablasDinamicas().subscribe({
      next: (data: TablasDinamicasConfig[]) => {
        const idColumns = {
          name: "id",
          type: ColumnType.Number,
          displayName: "ID"
        } as IColumnDinamico;
        data.forEach(item => {
          item.datasetColumnas.push(idColumns);
        });
        this.tablasDinamicasConfig = data;
        this.tablasDinamicasConfigReady = true
        this.tryLoadSavedData();
      }
    });
  }

  private getForm() {
    this._fs.getFormularioConControles(this.tipoTramite.formularioId, this.tipoTramite.formularioVersion).subscribe({
      next: (data: IFormularioConControles[]) => {
        this.formularioControles = data;
        this.formularioControlesReady = true;
        this.tryLoadSavedData();
      }
    });
  }

  load(data: ICrudTablaDinamicaConfig[], variablesSeguimiento: IConfiguracionLLaves[]): void {
    this.toLoad = data;
    this.variablesSeguimiento = variablesSeguimiento.map(i => {
      return {
        controlLabel: i.name,
        controlPath: "seguimiento:" + i.value
      } as ControlDTO;
    });
    this.tryLoadSavedData();
  }

  private tryLoadSavedData() {
    if (this.toLoad && this.tablasDinamicasConfigReady && this.formularioControlesReady) {
      this.loadSavedData()
    }
  }

  private loadSavedData(): void {
    if (!this.toLoad || !this.formularioControles || !this.tablasDinamicasConfig) return;
    this.toLoad.forEach((config: ICrudTablaDinamicaConfig) => {
      const formIndex = this.formularioControles.findIndex(
        f => f.id === config.formularioId
      );
      if (formIndex === -1 || !config.tablaDinamicaConfigId) return;

      const form = this.formularioControles[formIndex];
      this.setTablaId(form.id, config.tablaDinamicaConfigId);

      setTimeout(() => {
        const actualizarComponent = this.tablaParaActualizacionIdLlaves.toArray()[formIndex];
        const filtrosComponent = this.tablaFiltrosLlaves.toArray()[formIndex];
        if (actualizarComponent) {
          this.initializeTablaParaActualizacionLlaves(actualizarComponent, config.tablaDinamicaConfigId, formIndex, true);
          let llaves: IConfiguracionLLaves[] = [];
          config.llaves.forEach(savedLlave => {
            llaves.push({ value: savedLlave.value, name: savedLlave.name } as IConfiguracionLLaves);
          });
          //se crea una nueva lista para que angular detecte el cambio
          actualizarComponent.llaves = llaves;
        }
        if (filtrosComponent) {
          this.initializeTablaParaActualizacionLlaves(filtrosComponent, config.tablaDinamicaConfigId, formIndex, true);
          if (!config.filtros) return;
          let llaves: IConfiguracionLLaves[] = [];
          config.filtros.forEach(savedLlave => {
            llaves.push({ value: savedLlave.value, name: savedLlave.name } as IConfiguracionLLaves);
          });
          //se crea una nueva lista para que angular detecte el cambio
          filtrosComponent.llaves = llaves;
        }
      });

    });
    this.toLoad = null;
  }

  private initializeTablaParaActualizacionLlaves(component: ConfiguracionLlavesComponent, tablaId: number, formIndex: number, reset: boolean = true) {
    if (!component) {
      return;
    }

    if (reset) {
      component.reset();
    }

    const selectedTabla = this.tablasDinamicasConfig.find(t => t.id === tablaId);
    if (!selectedTabla) return;

    // Parse the tabla dinamica columns from JSON string
    const columns: IColumnDinamico[] = selectedTabla.datasetColumnas ?? [];

    // Get the specific form controls for this formularioControles item
    const currentForm = this.formularioControles[formIndex];
    if (!currentForm) return;

    let controlesFormulario = currentForm.controls ? [...currentForm.controls] : [];
    if (this.tipoTramite.componentesPersonalizadosParametros) {
      for (const param of this.tipoTramite.componentesPersonalizadosParametros) {
        const control = {
          controlPath: "componente:" + param,
          controlLabel: "componente:" + param
        } as ControlDTO
        controlesFormulario.push(control);
      }
    }

    controlesFormulario = [...controlesFormulario, ...this.variablesSeguimiento];

    const control = {
      controlPath: "accion:remover_valor",
      controlLabel: "accion:remover_valor"
    } as ControlDTO
    controlesFormulario.push(control);

    const keys = {
      data: columns,
      optionLabel: 'displayName',
      optionValue: 'name',
      headerText: this.trans.columnasTablaDinamica
    } as LlavesOptions;

    const values = {
      data: controlesFormulario,
      optionLabel: 'controlLabel',
      optionValue: 'controlPath',
      headerText: this.trans.controlesFormulario
    } as LlavesOptions;

    component.loadOptions(keys, values);
  }
  //#endregion load
}
